import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

const PACKAGE_LABELS = {
  trial:   { name: "7-Day Trial",     days: 7   },
  monthly: { name: "Monthly Premium", days: 30  },
  yearly:  { name: "Yearly Premium",  days: 365 },
};
const OD_PACK_LABELS = {
  starter: "Starter Spark",
  ember:   "Ember Pack",
  blaze:   "Blaze Pack",
  phoenix: "Phoenix Pack",
};

export default function BillingSuccess() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  // For tips (anonymous), the polling endpoint is different. We don't know
  // upfront — try authed first, fall back to public tip endpoint if 401/403.
  const isTipFlow = !user;
  const [state, setState] = useState({ phase: "checking", data: null, error: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", error: "Missing session_id." });
      return;
    }
    let active = true;

    async function poll() {
      try {
        // Tips are anonymous — use the public tip-status endpoint
        const url = isTipFlow
          ? `${API}/billing/tip-status/${sessionId}`
          : `${API}/billing/status/${sessionId}`;
        const opts = isTipFlow ? {} : { withCredentials: true };
        const { data } = await axios.get(url, opts);
        if (!active) return;
        if (data.payment_status === "paid") {
          setState({ phase: "success", data, error: null });
          if (!isTipFlow) await refreshUser();
          if (data.just_fulfilled || isTipFlow) {
            const ptype = data.purchase_type;
            if (ptype === "tip") {
              toast.success("💖 Tip sent — thanks for the love!", { duration: 6000 });
            } else if (ptype === "od_pack") {
              toast.success("☯️ Omni Dollars added to your wallet!", { duration: 5000 });
            } else if (ptype === "shop_usd") {
              toast.success("🛍️ Purchase delivered to your Nest!", { duration: 6000 });
            } else {
              // Premium upgrade — broadcast bullhorn
              window.dispatchEvent(new CustomEvent("omni:premium-unlock", {
                detail: {
                  userName: (user && user.name) || "An athlete",
                  packageId: data.package_id,
                }
              }));
              toast.success("🎉 Welcome to OmniMuscle Premium!", { duration: 6000 });
            }
          }
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState({ phase: "failed", data, error: "Payment was not completed." });
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", data, error: "Still waiting for Stripe..." });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (!active) return;
        setState({ phase: "error", error: e?.response?.data?.detail || "Verification failed." });
      }
    }
    poll();
    return () => { active = false; };
  }, [sessionId]);

  const isOdPack = state.data?.purchase_type === "od_pack";
  const pkg = state.data && !isOdPack ? PACKAGE_LABELS[state.data.package_id] : null;
  const odPackLabel = state.data && isOdPack ? OD_PACK_LABELS[state.data.pack_id] : null;

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white flex items-center justify-center px-4" data-testid="billing-success-page">
      <div className="bg-[#0F0F0F] border-2 border-[#D4AF37]/40 max-w-lg w-full p-10 text-center">
        {state.phase === "checking" && (
          <>
            <div className="text-6xl mb-4 animate-pulse">🐦‍🔥</div>
            <h1 className="font-display text-2xl text-white mb-2">Lighting the Phoenix...</h1>
            <p className="text-sm text-neutral-400">Confirming your payment with Stripe.</p>
          </>
        )}
        {state.phase === "success" && (
          <>
            <div className="text-7xl mb-4">{isOdPack ? "☯️" : "🐦‍🔥"}</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
              {isOdPack ? "Wallet Topped Up" : "Premium Unlocked"}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl text-white mb-4">
              {isOdPack ? (
                <>
                  Omni Dollars <span className="text-[#D4AF37]">Delivered.</span>
                </>
              ) : (
                <>
                  Welcome to the <span className="text-[#D4AF37]">Inner Circle.</span>
                </>
              )}
            </h1>
            <p className="text-sm text-neutral-400 mb-6">
              {isOdPack ? (
                <>
                  {odPackLabel || "Pack"} loaded.
                  <br />
                  Phoenix Tokens bonus included.{" "}
                  <Link to="/shop" className="text-[#D4AF37] underline">Go shopping →</Link>
                </>
              ) : (
                <>
                  {pkg ? `${pkg.name} (${pkg.days} days) activated.` : "Premium activated."}
                  <br />
                  <span className="text-[#D4AF37] font-bold">+1000 Phoenix Tokens</span> deposited and your Inner Circle badge has been awarded.
                </>
              )}
            </p>
            <button
              data-testid="success-cta-profile"
              onClick={() => navigate(isOdPack ? "/shop" : "/profile")}
              className="bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-sm py-3 px-6 transition-all"
            >
              {isOdPack ? "Visit Shop" : "See My Profile"}
            </button>
          </>
        )}
        {(state.phase === "failed" || state.phase === "timeout") && (
          <>
            <div className="text-6xl mb-4">💨</div>
            <h1 className="font-display text-2xl text-white mb-2">
              {state.phase === "timeout" ? "Still Processing" : "Payment Not Completed"}
            </h1>
            <p className="text-sm text-neutral-400 mb-6">
              {state.error}
              {state.phase === "timeout" && (
                <span className="block mt-2 text-neutral-500">
                  This sometimes takes a minute. Refresh or check your profile in a moment.
                </span>
              )}
            </p>
            <div className="flex gap-3 justify-center">
              <Link
                to="/plans"
                className="border border-zinc-700 hover:border-white text-white font-bold uppercase tracking-widest text-xs px-4 py-3 transition-colors"
              >
                Try Again
              </Link>
              <Link
                to="/profile"
                className="bg-white hover:bg-neutral-200 text-[#0A0A0A] font-bold uppercase tracking-widest text-xs px-4 py-3 transition-colors"
              >
                My Profile
              </Link>
            </div>
          </>
        )}
        {state.phase === "error" && (
          <>
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="font-display text-2xl text-white mb-2">Something Went Wrong</h1>
            <p className="text-sm text-red-400 mb-6">{state.error}</p>
            <Link
              to="/plans"
              className="bg-white hover:bg-neutral-200 text-[#0A0A0A] font-bold uppercase tracking-widest text-xs px-4 py-3 transition-colors inline-block"
            >
              Back to Plans
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
